package com.siriusxm.pia.views.unifiedaggregator.smithy

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.ImageFormat
import com.siriusxm.cmc.utils.ResizeParams
import com.siriusxm.pia.Application
import com.siriusxm.pia.components.HeaderSize
import com.siriusxm.pia.components.SVGIcons
import com.siriusxm.pia.components.box
import com.siriusxm.pia.components.svgIcon
import com.siriusxm.pia.rest.unifiedaggregator.ImageRenderingHelper
import com.siriusxm.pia.rest.unifiedaggregator.decodeImages
import com.siriusxm.pia.rest.unifiedaggregator.flatten
import com.siriusxm.pia.views.unifiedaggregator.AggregatorStyles
import com.siriusxm.pia.views.unifiedaggregator.resizedUrl
import com.siriusxm.smithy4kt.SmithyEntity
import contentingestion.unifiedmodel.Image
import contentingestion.unifiedmodel.LegacyImage
import contentingestion.unifiedmodel.LegacyImageList
import kotlinx.browser.window
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.JsonElement
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text


/**
 * Renders an image purposes map
 */
class ImagePurposeMapViewer(shape: SmithyEntity) : SmithyViewer<SmithyEntity>(shape) {
    @Composable
    override fun view(value: JsonElement?, options: SmithyViewOptions) {
        val flattened = value?.decodeImages()?.flatten() ?: return

        val imageGroups = flattened.groupBy {
            it.imageVariantType
        }

        Div({
            style {
                paddingTop(10.px)
                paddingRight(10.px)
            }
        }) {
            imageGroups.forEach { (type, flattenedImages) ->
                Div {
                    box(type.name, {
                        header({
                            titleSize = HeaderSize.SMALL
                        }, null)
                    }) {
                        Div {
                            flattenedImages.forEach {
                                expandableImageContainer(
                                    it.image.resizedUrl(),
                                    "${it.purpose} ${it.ratio.name.removePrefix("aspect_")}"
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

class ImageViewer(shape: SmithyEntity) : CustomTypeRenderer<Image>(shape, Image.serializer()) {
    @Composable
    override fun viewType(value: Image?, options: SmithyViewOptions) {
        if (value == null) return

        Div({ classes(AggregatorStyles.imageContainer) }) {
            Img(value.resizedUrl())
        }
    }
}

/**
 * Displays an image thumbnail that expands to full size on click.
 * Shows a broken image placeholder with a photo-off icon when the image fails to load.
 */
@Composable
fun expandableImageContainer(url: String, label: String? = null, title: String? = null) {
    Div({ classes(AggregatorStyles.imageBox) }) {
        var showingFull by remember { mutableStateOf(false) }
        var imageError by remember { mutableStateOf(false) }

        if (imageError) {
            Div({ classes(AggregatorStyles.imageError) }) {
                svgIcon(SVGIcons.photoOff) {
                    size = 48.px
                }
                Text("Image not found")
            }
        } else {
            Div({ classes(AggregatorStyles.imageContainer) }) {
                Img(url) {
                    onClick {
                        showingFull = true
                    }
                    addEventListener("error") {
                        imageError = true
                    }
                }
            }
        }
        if (label != null) {
            Div({ classes(AggregatorStyles.imageLabel) }) {
                Text(label)
            }
        }

        if (showingFull && !imageError) {
            Div({
                classes(AggregatorStyles.imageFullscreenOverlay)
                onClick {
                    showingFull = false
                }
            }) {
                Img(url) {
                    if (title != null) {
                        title(title)
                    }
                    classes(AggregatorStyles.imageFullscreenContent)
                }
            }
        }
    }
}

/**
 * Maps legacy HTTP image hostnames to the CloudFront path prefix that proxies them
 * over HTTPS. The CMC distribution has a behavior at `/legacy-images/{prefix}/...`
 * that forwards to the corresponding HTTP-only origin.
 */
private val LEGACY_IMAGE_HOSTS = mapOf(
    "pri.art.prod.streaming.siriusxm.com" to "prod",
    "pri.art.uat.streaming.siriusxm.com" to "uat",
)

/**
 * Rewrites HTTP URLs from known legacy image hosts through the CMC CloudFront
 * distribution to avoid mixed-content browser errors. URLs that don't start with
 * http:// or that point to an unknown host pass through unchanged.
 */
private fun proxyImageUrl(url: String): String {
    if (!url.startsWith("http://")) return url
    val rest = url.removePrefix("http://")
    val slashIdx = rest.indexOf('/')
    val host = if (slashIdx == -1) rest else rest.substring(0, slashIdx)
    val path = if (slashIdx == -1) "" else rest.substring(slashIdx)
    val prefix = LEGACY_IMAGE_HOSTS[host.lowercase()] ?: return url
    return "${window.location.origin}/legacy-images/$prefix$path"
}

/**
 * Renders a list of legacy images with proper URL resolution.
 * Handles three URL formats:
 * - Absolute HTTP URLs from known legacy image hosts are routed through the
 *   CMC CloudFront `/legacy-images/...` behavior to upgrade them to HTTPS.
 * - Other absolute URLs (HTTPS, or HTTP from unknown hosts) are used directly.
 * - Relative paths (e.g., "/clips/coffeehouse/...") are resolved through the
 *   atlas image server CDN with default resize parameters.
 */
class LegacyImagesViewer(shape: SmithyEntity) :
    CustomTypeRenderer<LegacyImageList>(shape, ListSerializer(LegacyImage.serializer())) {
    @Composable
    override fun viewType(value: LegacyImageList?, options: SmithyViewOptions) {
        if (value == null) return
        val baseUrl = Application.configuration.atlasImageServerBaseUrl
        Div {
            value.forEach {
                val fullUrl = if (it.url.startsWith("http://") || it.url.startsWith("https://")) {
                    proxyImageUrl(it.url)
                } else {
                    ImageRenderingHelper.getImageUrl(
                        baseUrl, it.url,
                        ResizeParams(it.width ?: 300, it.height ?: 300),
                        ImageFormat.JPEG
                    )
                }
                expandableImageContainer(fullUrl, it.name)
            }
        }
    }
}